import { useEffect } from "react";

/**
 * Hardcoded fallback Clarity project ID.
 *
 * Mirrors the FALLBACK_META_PIXEL_ID pattern in analytics.ts: if neither the
 * VITE_CLARITY_PROJECT_ID env var nor the ngu_config.clarityProjectId DB value
 * is set, this hardcoded production ID is used so Clarity session recording
 * is never silently dropped in misconfigured builds.
 *
 * Precedence at runtime:  env (VITE_CLARITY_PROJECT_ID) → DB (ngu_config.clarityProjectId) → hardcoded fallback
 *
 * The analytics-env-check script validates this value is non-empty at deploy time.
 */
const FALLBACK_CLARITY_TAG_ID = "whcafqqjb8";

const ENV_CLARITY_TAG_ID: string = import.meta.env.VITE_CLARITY_PROJECT_ID || "";

function injectClarity(tagId: string) {
  if (!tagId) return;
  const src = `https://www.clarity.ms/tag/${tagId}`;
  if (document.querySelector(`script[src="${src}"]`)) return;

  (function(c: any, l: Document, a: string, r: string, i: string) {
    c[a] = c[a] || function(...args: any[]) {
      (c[a].q = c[a].q || []).push(args);
    };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = `https://www.clarity.ms/tag/${i}`;
    const y = l.getElementsByTagName(r)[0];
    if (y?.parentNode) {
      y.parentNode.insertBefore(t, y);
    } else {
      l.head.appendChild(t);
    }
  })(window, document, "clarity", "script", tagId);
}

export function useMicrosoftClarity() {
  useEffect(() => {
    // Env wins — inject immediately and skip the network round-trip.
    if (ENV_CLARITY_TAG_ID) {
      injectClarity(ENV_CLARITY_TAG_ID);
      return;
    }
    // No env var — try the DB value, then fall back to the hardcoded ID.
    let cancelled = false;
    fetch("/api/netglowup/config")
      .then(r => (r.ok ? r.json() : null))
      .then((cfg: { clarityProjectId?: string | null } | null) => {
        if (cancelled) return;
        const dbId = cfg?.clarityProjectId?.trim();
        injectClarity(dbId || FALLBACK_CLARITY_TAG_ID);
      })
      .catch(() => {
        if (cancelled) return;
        injectClarity(FALLBACK_CLARITY_TAG_ID);
      });
    return () => { cancelled = true; };
  }, []);
}

export function clarityEvent(eventName: string) {
  try {
    const w = window as Window & { clarity?: (...args: unknown[]) => void };
    if (typeof w.clarity === "function") {
      w.clarity("event", eventName);
    }
  } catch {
    // non-fatal: Clarity may not be loaded or may throw in restricted environments
  }
}

/**
 * AT-007 AUDIT ANCHOR — scripts/analytics-tracking-audit.mjs checkClarityIdentify()
 * searches for both `clarityIdentify(` (call site) and `{ clarityIdentify }` / `clarityIdentify`
 * in import statements across the NGU funnel files.  If this function is renamed or moved,
 * update the AT-007 check in the audit script at the same time so the audit stays honest.
 *
 * Privacy: the raw email is NEVER forwarded to Clarity.  A one-way SHA-256 hash of the
 * lower-cased, trimmed email is computed client-side; only the first 32 hex characters
 * (128 bits — sufficient for session correlation, not reversible to the original email)
 * are sent.  This satisfies the Clarity "identify" API while ensuring no PII leaves the
 * browser to a third-party analytics service.
 */
export function clarityIdentify(email: string): void {
  const normalised = email.toLowerCase().trim();
  if (!normalised) return;

  crypto.subtle
    .digest("SHA-256", new TextEncoder().encode(normalised))
    .then((buf) => {
      const pseudonym = Array.from(new Uint8Array(buf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("")
        .slice(0, 32);
      const w = window as Window & { clarity?: (...args: unknown[]) => void };
      if (typeof w.clarity === "function") {
        w.clarity("identify", pseudonym);
      }
    })
    .catch(() => {
      // non-fatal: crypto.subtle unavailable or Clarity not loaded
    });
}
